/**
 * "What did this institution say" — one submission, in full, read-only.
 *
 * ── WHY A MODAL AND NOT A DETAIL ROUTE ──────────────────────────────────────
 *
 * ⛔ **`App.tsx` declares no `/event-interest/:id`**, so a link would land on
 * `NotFound` — which reads as the record being missing rather than as the page
 * not existing. That is the affordance-with-one-outcome this repo refuses
 * everywhere (Xero's `sync`, DocuSign's absent connect), and `/credit-notes`
 * records the same decision for the same reason. ⚠️ **When a detail route
 * lands, this component is what moves onto it**; nothing here assumes a dialog.
 *
 * ── READ-ONLY, EXCEPT FOR ONE CONTROL IT DOES NOT OWN ───────────────────────
 *
 * 🔴 **A DELETE IS OFFERED IN THE FOOTER, on the owner's decision.** ⚠️
 * *SUPERSEDED: "⛔ **There is no edit, no delete and no un-submit, and none may
 * be added here** ... This component takes no callback that could write."*
 *
 * ⚠️ **AND THE REST OF THAT SENTENCE STILL HOLDS.** There is no edit and no
 * un-submit: a submission is the record of what a customer said, so removing
 * the record is a decision somebody may take and REWRITING it is not. Ruling 4
 * is untouched — a rejected company's submissions survive — and the control
 * confirms through a dialog naming every project of interest a delete takes the
 * company off. (Removing a project of interest closes no sales pursuit since
 * the owner's decision of 2026-10-12.)
 *
 * ⛔ **THIS COMPONENT STILL WRITES NOTHING ITSELF.** It renders whatever is
 * handed to {@link SubmissionDetailModalProps.actions} and knows nothing about
 * the request, the permission or the wording — so it stays a view over a view
 * model, and a second surface can mount the same control without this dialog.
 *
 * ── THE THREE FACTS A READER CANNOT GET FROM THE FIGURES ────────────────────
 *
 * Each is stated on screen, and each is the documented *fact about the data*
 * exception to the no-helper-text prohibition rather than a carve-out from it:
 *
 * 1. **A festival with no row was NEVER ASKED, not declined.** The form region
 *    is what says which festivals were on offer, so it is rendered beside the
 *    answers rather than buried in the audit block.
 * 2. **`All cities on this tour` STORES NO CITIES.** An empty city list under a
 *    whole-tour answer is the answer, not a gap — see `selectionLabel`.
 * 3. **No deal does not mean nothing happened.** `deal_id` is
 *    `ON DELETE SET NULL`, so `null` is *no deal, or the deal was deleted*;
 *    what says whether the submission was processed is `pursuitsCreatedAt`.
 */

import type { ReactNode } from 'react';
import { Badge, Modal, Skeleton, ValuePills, namePills, type BadgeTone } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { formatDateTime, orDash } from '@/lib/format';
import type { CompanyApprovalStatus, EventInterestFormRegion } from '@/lib/types';
import {
  formRegionLabel,
  inclusionLabel,
  noCitiesReason,
  responseLabel,
  selectionLabel,
} from './event-interest-wording';

/**
 * One festival this submission answered.
 *
 * ⚠️ **A VIEW MODEL, so every wire field name is spelled once in the page's
 * adapter** — see `DemandCityRow` for the reasoning, which is the same here and
 * matters more: `inclusion` and `selection` are two nullable strings one line
 * apart, and a renderer reading the wrong one prints a plausible answer to a
 * question that was never asked.
 */
export interface SubmissionFestivalAnswer {
  /** Stable key — the project id, which is the row's identity on the wire. */
  key: string;
  festivalName: string;
  /** `'yes'` or `'no'`, verbatim. Rendered through `responseLabel`. */
  response: string;
  /** `null` when the question was not asked — NOT "participation only". */
  inclusion: string | null;
  /** `null` on a festival that runs in one place — NOT "no cities". */
  selection: string | null;
  cityNames: readonly string[];
  /**
   * The string `admin-app` sent.
   *
   * ⛔ **AUDIT ONLY, AND IT IS JOINED ON BY NOTHING** (contract §3). It is
   * shown so that what a customer's form submitted is never lost, and it is
   * shown as a muted aside rather than as the festival's identity — the
   * project is the identity, and this codebase has been bitten four times by
   * matching on admin-editable text.
   */
  eventKey: string;
}

/** One submission, as this dialog renders it. */
export interface SubmissionDetail {
  id: number;
  companyName: string;
  approvalStatus: CompanyApprovalStatus;
  contactName: string;
  contactEmail: string;
  contactDesignation: string | null;
  contactPhone: string | null;
  /**
   * `'north-america'` or `'global'`; rendered through `formRegionLabel`.
   *
   * ⚠️ **TYPED TO THE UNION rather than to `string`**, so a stale spelling at
   * any call site is a compile error rather than a label that renders the raw
   * slug. The hyphen is `admin-app`'s and is load-bearing: `BillingRegion`
   * carries `north_america` with an UNDERSCORE one character away, and
   * `eventKey` uses underscores too. All three are correct and none may be
   * normalised towards another.
   */
  formRegion: EventInterestFormRegion;
  /** `admin-app`'s natural key, and the whole idempotency story. */
  sourceReference: string;
  submittedAt: string;
  consentText: string;
  consentVersion: string;
  consentedAt: string;
  /** `null` is *no deal, or the deal was deleted* — never *not processed*. */
  dealId: number | null;
  /**
   * 🔴 **THE DISCRIMINATOR THAT MAKES {@link dealId} READABLE**, and the pair is
   * the only thing separating a deal somebody deleted from one that was never
   * raised:
   *
   * | `dealId` | `dealCreatedAt` | means |
   * | --- | --- | --- |
   * | `null` | `null` | **not attempted, or it FAILED** |
   * | `null` | set | created and **since deleted** — ordinary |
   * | set | set | normal |
   *
   * ⚠️ **It is not cleared when the deal is deleted**, deliberately: the whole
   * point is that it outlives the row it describes.
   *
   * ✅ **THE SERVER SENDS IT** — migration
   * `225_add_event_interest_deal_created_at.sql`, applied 2026-09-18, and the
   * repository's mapper emits it. *SUPERSEDED, measured earlier the same day:
   * "🔴 **OPTIONAL: NO SERVER HAS EVER SENT IT** — the backend emits `dealId`
   * and `pursuitsCreatedAt` and no third field."*
   *
   * ⛔ **IT STAYS OPTIONAL, AND SO DOES THE `typeof` TEST.** This app deploys
   * separately from the API and can be NEWER than it, so the key can be absent
   * again on a rollback — and `!== null` is TRUE for an absent key, which is
   * exactly the false badge this pair exists to prevent. Read it with `typeof`,
   * never `!== null`; see {@link DealState} and the full note on
   * `EventInterestSubmission.dealCreatedAt` in `types.ts`.
   */
  dealCreatedAt?: string | null;
  /** When the pursuits and the deal were raised, or `null`. */
  pursuitsCreatedAt: string | null;
  festivals: readonly SubmissionFestivalAnswer[];
}

/**
 * The company's approval state, worded from the fact rather than from a
 * judgement.
 *
 * ⚠️ **A REJECTED COMPANY'S SUBMISSION SURVIVES AND IS NOT AN ERROR** (contract
 * ruling 4) — it is toned `neutral` rather than `danger` for that reason: the
 * institution answered the form in good faith and the record of it stands. What
 * `rejected` decides is that no pursuits and no deal will ever be raised.
 */
const APPROVAL_TONE: Record<CompanyApprovalStatus, BadgeTone> = {
  approved: 'success',
  pending: 'warning',
  rejected: 'neutral',
};

/**
 * ⚠️ **"Awaiting approval", never "Unapproved" or "Pending".** A pending
 * institution has not been refused — nobody has looked at it — and the wording
 * on this badge is the same one the demand columns use, so one state is not
 * called two things on two screens.
 */
const APPROVAL_LABEL: Record<CompanyApprovalStatus, string> = {
  approved: 'Approved',
  pending: 'Awaiting approval',
  rejected: 'Rejected',
};

/**
 * What became of the submission after it was stored.
 *
 * 🔴 **`dealId` ALONE CANNOT ANSWER THIS AND MUST NOT BE ASKED TO.** The column
 * is `ON DELETE SET NULL` because a deal is working state somebody may
 * legitimately delete, while the submission is the record of what was said and
 * must not be held hostage to it. So a `null` deal on a processed submission is
 * an ordinary state, and reading it as "nothing happened" is the proxy-field
 * mistake this repo records five times over: a field written to answer one
 * question, consulted for another.
 *
 * ⚠️ **`pursuitsCreatedAt` is the field written to answer it**, and the three
 * branches below are the three cases the contract names in §6 — already
 * approved (done now), pending (done when an approver approves), rejected
 * (never).
 */
function ProcessingState({ submission }: { submission: SubmissionDetail }) {
  if (submission.pursuitsCreatedAt !== null) {
    return (
      <span className="text-sm text-fg-secondary">
        Projects of interest added {formatDateTime(submission.pursuitsCreatedAt)}
      </span>
    );
  }
  if (submission.approvalStatus === 'rejected') {
    /*
     * 🔴 **"AND NEVER WILL BE" IS THE HALF THAT MAKES THIS BRANCH WORTH
     * HAVING.** A rejected company's submission survives deliberately (contract
     * ruling 4) and nothing is ever raised for it — so wording this the way the
     * pending branch is worded would promise an outcome that is not coming.
     * `pursuitsCreatedAt` is `null` on both and cannot tell them apart;
     * `approvalStatus` is what does.
     */
    return (
      <span className="text-sm text-fg-secondary">
        Not added, and will not be — this institution was rejected. The submission is kept as the
        record of what was said.
      </span>
    );
  }
  if (submission.approvalStatus === 'pending') {
    return (
      <span className="text-sm text-fg-secondary">
        Not added yet — the projects of interest and the deal are created when somebody approves
        this institution.
      </span>
    );
  }
  /*
   * ⚠️ **APPROVED, AND STILL NOTHING — THE ONE ANOMALOUS BRANCH, NAMED RATHER
   * THAN DIAGNOSED.** Both follow-ups are best-effort and neither may fail the
   * submission, so one that did not run leaves a log line and nothing else.
   * This sentence states the two facts a reader has and stops there; guessing
   * at a cause would be this screen inventing one.
   */
  return (
    <span className="text-sm text-fg-secondary">
      Not added, though this institution is approved.
    </span>
  );
}

/**
 * The deal, read from the PAIR.
 *
 * 🔴 **`dealId` ALONE CANNOT SAY WHICH OF THREE THINGS HAPPENED, AND THE MIDDLE
 * ROW IS THE ONE THAT MATTERS.** `dealId` is `ON DELETE SET NULL` because a
 * deal is working state somebody may legitimately delete, so a `null` there is
 * *no deal, or a deal since deleted* — and the deal is a BEST-EFFORT step that
 * may never fail the submission, so one that was never raised leaves no error
 * anywhere. Without `dealCreatedAt` those two are byte-identical and the
 * failure is invisible for ever.
 *
 * ⛔ **AND THIS IS WHY THERE IS NO "raise the deal" CONTROL HERE.** Offering
 * one on the deleted row would raise a SECOND deal for a submission that is
 * meant to have exactly one (contract ruling 7) — and this page is read-only in
 * any case.
 */
function DealState({ submission }: { submission: SubmissionDetail }) {
  if (submission.dealId !== null) {
    return <span className="tabular">#{submission.dealId}</span>;
  }
  /*
   * 🔴 **`typeof === 'string'`, AND NEVER `!== null` — this branch shipped the
   * false statement it exists to prevent.** The key was absent from the wire,
   * so it read `undefined`, `undefined !== null` is **true**, and this arm
   * fired on EVERY submission with no deal: *"Raised Invalid Date, and since
   * deleted"* about a deal that was never raised, with the honest branch below
   * it unreachable.
   *
   * ⚠️ **A confident wrong answer, not a crash, which is why it survived** —
   * and `tsc` was green throughout, because it checks this type against its
   * callers in this repo and never against the mapper that fills it. **Grep the
   * emitter, not the type.**
   *
   * ✅ **THE KEY ARRIVED AND THIS ARM WORKS NOW, WITH NO FURTHER CHANGE** —
   * migration `225_add_event_interest_deal_created_at.sql`, applied 2026-09-18.
   * *SUPERSEDED, the cost that was being accepted in the meantime: "until the
   * server sends the field, a deal somebody really did delete reads as 'Not
   * raised' below. That is an UNDER-report of a rare state."* **That prediction
   * was the whole argument for the safe direction and it paid out exactly** —
   * the branch needed no edit when the field landed, which is what choosing the
   * under-reporting test buys.
   *
   * ⛔ **DO NOT NOW "TIDY" IT TO `!== null` ON THE STRENGTH OF THE FIELD
   * EXISTING.** The key is still optional, this app can deploy ahead of the
   * API, and on such a server the wrong answer returns in full.
   */
  if (typeof submission.dealCreatedAt === 'string') {
    /* Ordinary, not a fault: somebody deleted the deal and the stamp outlives
       it, which is the whole reason the stamp is not cleared. */
    return (
      <span className="text-fg-muted">
        Raised {formatDateTime(submission.dealCreatedAt)}, and since deleted
      </span>
    );
  }
  /*
   * ⚠️ **"Not raised", and deliberately NOT "failed".** Three situations reach
   * here — waiting on an approver, a rejected company where it never will be,
   * and a best-effort step that did not run — and nothing on this record
   * separates the third from the first two. "What happened next" above carries
   * the company's own state, which is what explains it.
   */
  return <span className="text-fg-muted">Not raised</span>;
}

/**
 * "What they agreed to" — the consent record, **collapsed**.
 *
 * ⛔ **THIS IS A STORED VALUE RENDERED, NOT HELPER TEXT, AND A SWEEP MUST NOT
 * TAKE IT.** It is the same class as any other field on this record: the
 * wording the person actually agreed to, kept frozen precisely so that *what
 * somebody agreed to* is answerable after the wording changes. Storing it and
 * then rendering it nowhere defeats the reason it is stored.
 *
 * ⚠️ **COLLAPSED RATHER THAN INLINE, AND THAT IS THE OWNER'S RULING.** A wall
 * of legal text in the reading path of a triage screen is how a section stops
 * being read at all — including the answers underneath it, which are what this
 * dialog is opened for. The summary line carries the version and the moment,
 * which are the two facts a reader usually wants; the text itself is one press
 * away.
 *
 * ⚠️ **A NATIVE `<details>`, not a state toggle.** It opens without JavaScript,
 * it is in the tab order, a browser's find-in-page can open it, and print
 * styles expand it — none of which a `useState` disclosure gets for free. The
 * marker is hidden and replaced by a chevron so the control reads the same in
 * every browser.
 *
 * ⛔ **THE TEXT IS VERBATIM AND WHOLE.** Never truncated, never paraphrased,
 * never re-derived from today's wording: a shortened copy of what somebody
 * consented to is worth nothing as a record, and rendering the CURRENT consent
 * text against an OLD submission would be a claim about what they agreed to
 * that is simply false.
 */
function ConsentDisclosure({ submission }: { submission: SubmissionDetail }) {
  return (
    <details className="group rounded-md border border-border">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 text-sm font-semibold text-fg transition-colors duration-150 ease-out hover:bg-surface-hover motion-reduce:transition-none [&::-webkit-details-marker]:hidden">
        <span className="min-w-0">
          What they agreed to
          <span className="ml-2 text-xs font-normal text-fg-muted">
            Version {submission.consentVersion}
            <span className="mx-1.5 text-fg-subtle">·</span>
            <span className="tabular">{formatDateTime(submission.consentedAt)}</span>
          </span>
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-fg-subtle transition-transform duration-150 ease-out group-open:rotate-180 motion-reduce:transition-none"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </summary>
      <p className="m-0 border-t border-border bg-surface-sunken px-3 py-2 text-xs text-fg-secondary [overflow-wrap:anywhere]">
        {submission.consentText}
      </p>
    </details>
  );
}

/** A labelled fact. Two columns from `sm`, stacked at 320. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      {/*
        ⚠️ **A LABEL NAMES A VALUE RATHER THAN EXPLAINING IT**, so it is not
        helper text. Without it a column of bare strings — an email, a
        designation, a reference — is unreadable.
      */}
      <dt className="m-0 text-xs font-medium text-fg-muted">
        {label}
      </dt>
      <dd className="m-0 mt-0.5 text-sm text-fg-secondary [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

/** One festival's answer. */
function FestivalAnswer({ answer }: { answer: SubmissionFestivalAnswer }) {
  const said = responseLabel(answer.response);
  const inclusion = inclusionLabel(answer.inclusion);
  const selection = selectionLabel(answer.selection);
  const saidYes = answer.response === 'yes';

  return (
    <li className="border-b border-border px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
            {answer.festivalName}
          </p>
          {/*
            ⚠️ **THE FORM'S OWN KEY, AS AN ASIDE.** Kept so what the customer's
            form submitted is never lost or rewritten, and kept muted because
            the PROJECT is the identity — nothing joins on this.
          */}
          <p className="m-0 mt-0.5 text-xs text-fg-subtle [overflow-wrap:anywhere]">
            <span className="sr-only">Key the form sent </span>
            {answer.eventKey}
          </p>
        </div>
        {/*
          ⚠️ **A `no` IS RENDERED AS AN ANSWER, never dropped.** "Said no" and
          "was never asked" are different facts and the second is the ABSENCE of
          a row — so dropping the noes would make a considered refusal
          indistinguishable from a festival the form never offered.
        */}
        <Badge tone={saidYes ? 'success' : 'neutral'}>{said}</Badge>
      </div>

      {saidYes && (
        <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          {/*
            `inclusion` is required on a `yes` (contract §5), so this should
            always be present here — but it is read rather than assumed, and a
            null prints the honest sentence instead of inventing a purchase.
          */}
          <Fact label="Includes">
            {inclusion ?? <span className="text-fg-muted">Not recorded</span>}
          </Fact>
          <Fact label="Cities">
            {answer.cityNames.length > 0 ? (
              <ValuePills items={namePills(answer.cityNames)} />
            ) : (
              /*
                🔴 **AN EMPTY LIST IS AN ANSWER, NOT A GAP, AND IT IS WORDED
                FROM THE SELECTION.** A whole-tour taker deliberately names no
                city — materialising "all" goes stale the moment a stop changes
                — and a single-location festival has none to name. Printing "no
                cities" would reverse the first answer.
              */
              <span className="text-fg-muted">{noCitiesReason(answer.selection)}</span>
            )}
          </Fact>
          {/*
            The selection is shown only where it adds something the cities do
            not already say: on `select_cities` the list above IS the answer.
          */}
          {selection !== null && answer.selection !== 'select_cities' && (
            <Fact label="Asked for">{selection}</Fact>
          )}
        </dl>
      )}
    </li>
  );
}

export interface SubmissionDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** `null` while the read is in flight — the dialog opens on skeletons. */
  submission: SubmissionDetail | null;
  loading: boolean;
  /** The server's own sentence, or ours naming what failed. */
  error: string | null;
  /**
   * Rendered in the dialog's footer.
   *
   * ⛔ **DRAWN ONLY ONCE A SUBMISSION HAS LOADED, and that is a correctness
   * requirement rather than tidiness.** The one control that goes here confirms
   * by naming the projects this submission's answers hold — so over skeletons
   * it would be offering to destroy something it cannot describe, and over the
   * error state it would be offering it for a record that failed to load.
   *
   * ⚠️ **Passing nothing leaves the footer OUT ENTIRELY**, rather than drawing
   * an empty bordered strip across the bottom of a read-only dialog.
   */
  actions?: ReactNode;
}

export function SubmissionDetailModal({
  open,
  onClose,
  submission,
  loading,
  error,
  actions,
}: SubmissionDetailModalProps) {
  const selectedFestivals = submission
    ? submission.festivals.filter((answer) => answer.response === 'yes')
    : [];
  return (
    <Modal
      open={open}
      onClose={onClose}
      {...(submission !== null && actions ? { footer: actions } : {})}
      /*
       * ⚠️ **The title names the INSTITUTION rather than the submission id.**
       * This dialog is opened from one row of twenty and the question it
       * answers is "what did THIS institution say"; an id is correct and
       * unrecognisable.
       */
      title={submission ? submission.companyName : 'Submission'}
      size="lg"
    >
      {/*
        ⚠️ **THE ERROR IS A `danger`-TONED BLOCK NAMING WHAT FAILED**, and it
        carries the server's own sentence. A dialog that opened and then showed
        nothing would read as an empty submission.
      */}
      {error && (
        <Notice
          tone="danger"
          role="alert"
          className="mb-3"
          title={<>Couldn&rsquo;t load this submission.</>}
        >
          <span className="[overflow-wrap:anywhere]">{error}</span>
        </Notice>
      )}

      {loading && submission === null ? (
        /* Skeletons in the SHAPE of the real content, never a spinner. */
        <div className="flex flex-col gap-3">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-3 w-56" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : submission === null ? (
        /* Not an EmptyState: a dialog with nothing in it has already failed,
           and the banner above says why. */
        <p className="m-0 text-sm text-fg-muted">Nothing to show.</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={APPROVAL_TONE[submission.approvalStatus]}>
              {APPROVAL_LABEL[submission.approvalStatus]}
            </Badge>
            {/*
              ⚠️ **THE REGION IS ON SCREEN, NOT IN AN AUDIT FOOTER, AND THAT IS
              THE POINT.** A festival absent from the answers below was either
              declined or never offered, and the region is the only thing that
              says which. Without it, "there are four answers" reads as a
              partial form.
            */}
            <Badge tone="neutral" variant="outline">
              {formRegionLabel(submission.formRegion)} form
            </Badge>
          </div>

          <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
            <Fact label="Contact">{submission.contactName}</Fact>
            <Fact label="Email">
              <EmailLink address={submission.contactEmail} />
            </Fact>
            <Fact label="Designation">{orDash(submission.contactDesignation)}</Fact>
            <Fact label="Phone">{orDash(submission.contactPhone)}</Fact>
            <Fact label="Submitted">{formatDateTime(submission.submittedAt)}</Fact>
            <Fact label="What happened next">
              <ProcessingState submission={submission} />
            </Fact>
          </dl>

          <section>
            <h3 className="m-0 mb-2 text-sm font-semibold text-fg">
              Festivals selected
              {/*
                ⚠️ **"SELECTED", not "of 7".** A denominator here would be this
                client asserting how many festivals the form offered, which is
                the form's business and varies by region — and a wrong
                denominator reads as missing answers. Only "yes" answers are
                shown (owner, 2026-10-16); "no" answers are hidden.
              */}
            </h3>
            {selectedFestivals.length === 0 ? (
              <p className="m-0 rounded-md border border-border px-4 py-3 text-sm text-fg-muted">
                No festival was selected on this submission.
              </p>
            ) : (
              <ul className="m-0 list-none rounded-md border border-border p-0">
                {selectedFestivals.map((answer) => (
                  <FestivalAnswer key={answer.key} answer={answer} />
                ))}
              </ul>
            )}
          </section>

          <ConsentDisclosure submission={submission} />

          <section>
            <h3 className="m-0 mb-2 text-sm font-semibold text-fg">Audit</h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
              {/*
                ⚠️ **The reference is what makes a replay a `409` rather than a
                second submission.** `admin-app` records every submission
                locally even when EMERGE is unreachable and replays it later,
                so the same submission WILL arrive twice; this is the natural
                key that stops that being two deals and two sets of pursuits.
              */}
              <Fact label="Source reference">
                <span className="tabular">{submission.sourceReference}</span>
              </Fact>
              <Fact label="Deal">
                <DealState submission={submission} />
              </Fact>
            </dl>
          </section>
        </div>
      )}
    </Modal>
  );
}
